import React, { useState, useEffect, useRef } from "react";
import { Send, MessageCircle, ShieldCheck } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import { useAuth } from "../../context/AuthContext";
import {
  chat as api,
  type PrivateMessage,
  type ChatContact,
  type ChatParticipantType,
  type ApiError,
} from "../../services/api";

const inp = {
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
};

interface ActiveThread {
  type: ChatParticipantType;
  id: string;
  label?: string;
}

const ChatPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [admins, setAdmins] = useState<ChatContact[]>([]);
  const [otherId, setOtherId] = useState("");
  const [activeThread, setActiveThread] = useState<ActiveThread | null>(null);
  const [messages, setMessages] = useState<PrivateMessage[]>([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!societyId) return;
    api.adminContacts(societyId).then(setAdmins).catch(() => {});
  }, [societyId]);

  const loadThread = async (type: ChatParticipantType, id: string, label?: string) => {
    if (!societyId || !id) return;
    setLoading(true);
    setError("");
    try {
      const msgs = await api.thread(societyId, type, id);
      setMessages(msgs);
      setActiveThread({ type, id, label });
    } catch (err) {
      setError((err as ApiError).message || "Failed to load conversation.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !activeThread || !body.trim()) return;
    setSending(true);
    try {
      const sent = await api.send(societyId, activeThread.type, activeThread.id, body.trim());
      setMessages((p) => [...p, sent]);
      setBody("");
    } catch (err) {
      setError((err as ApiError).message || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PageLayout>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
          Messages
        </h1>
        <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
          Message another resident, or your society admin
        </p>
      </div>

      {admins.length > 0 && (
        <div className="nx-card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--nx-text-secondary)", marginBottom: 8 }}>
            Society Admin
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {admins.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => loadThread("admin", a.id, a.fullName)}
                className="nx-btn nx-btn-secondary"
                style={{ gap: 6 }}
              >
                <ShieldCheck size={14} /> {a.fullName}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="nx-card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--nx-text-secondary)", marginBottom: 8 }}>
          Another Resident
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadThread("resident", otherId.trim());
          }}
          style={{ display: "flex", gap: 8 }}
        >
          <input
            value={otherId}
            onChange={(e) => setOtherId(e.target.value)}
            placeholder="Enter the other resident's ID to open a conversation"
            style={{ ...inp, flex: 1 }}
          />
          <button type="submit" className="nx-btn nx-btn-secondary">Open Chat</button>
        </form>
      </div>

      {error && <div style={{ color: "#EF4444", fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {activeThread && (
        <div className="nx-card" style={{ padding: 16, display: "flex", flexDirection: "column", height: 420 }}>
          {activeThread.label && (
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--nx-text-primary)", marginBottom: 8 }}>
              {activeThread.label}
            </div>
          )}
          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
            {loading && <div style={{ color: "var(--nx-text-muted)", fontSize: 13 }}>Loading…</div>}
            {!loading && messages.length === 0 && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, color: "var(--nx-text-muted)" }}>
                <MessageCircle size={28} />
                <p style={{ fontSize: 13, marginTop: 8 }}>No messages yet. Say hello!</p>
              </div>
            )}
            {messages.map((m) => {
              const mine = m.senderType === "resident" && m.senderId === user?.id;
              return (
                <div key={m.id} style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "70%" }}>
                  <div style={{
                    background: mine ? "var(--nx-accent)" : "var(--nx-surface-3)",
                    color: mine ? "#fff" : "var(--nx-text-primary)",
                    padding: "8px 12px", borderRadius: 12, fontSize: 13,
                  }}>
                    {m.body}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--nx-text-muted)", marginTop: 2, textAlign: mine ? "right" : "left" }}>
                    {new Date(m.createdAt).toLocaleTimeString()}
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={handleSend} style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <input value={body} onChange={e => setBody(e.target.value)} placeholder="Type a message…" style={{ ...inp, flex: 1 }} />
            <button type="submit" disabled={sending || !body.trim()} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
              <Send size={14} /> Send
            </button>
          </form>
        </div>
      )}
    </PageLayout>
  );
};

export default ChatPage;
